// Serviço de exames solicitados — alimenta o fluxo de retorno.
import { createClient } from '@/lib/supabase/client'
import { registrarAuditoria } from '@/services/identity'
import { registrarEvento } from '@/services/timeline'

export type StatusExame = 'solicitado' | 'realizado' | 'recebido' | 'analisado' | 'cancelado'

export interface ExameSolicitado {
  id: string
  workspace_id: string
  paciente_id: string
  consulta_id: string | null
  retorno_consulta_id: string | null
  nome: string
  status: StatusExame
  documento_id: string | null
  observacoes: string | null
  created_at: string
}

export const STATUS_EXAME_LABEL: Record<StatusExame, string> = {
  solicitado: 'Solicitado',
  realizado: 'Realizado',
  recebido: 'Recebido',
  analisado: 'Analisado',
  cancelado: 'Cancelado',
}

export async function listExamesDoPaciente(workspaceId: string, pacienteId: string): Promise<ExameSolicitado[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('exames_solicitados')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('paciente_id', pacienteId)
    .order('created_at', { ascending: false })
    .limit(100)
  if (error) throw error
  return (data ?? []) as ExameSolicitado[]
}

export async function solicitarExame(params: {
  workspaceId: string
  pacienteId: string
  consultaId?: string | null
  nome: string
}): Promise<string> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('exames_solicitados')
    .insert({
      workspace_id: params.workspaceId,
      paciente_id: params.pacienteId,
      consulta_id: params.consultaId ?? null,
      nome: params.nome.trim(),
    })
    .select('id')
    .single()
  if (error) throw error
  const id = (data as { id: string }).id
  await registrarEvento({
    workspaceId: params.workspaceId,
    pacienteId: params.pacienteId,
    consultaId: params.consultaId ?? null,
    tipo: 'exame_solicitado',
    titulo: `Exame solicitado: ${params.nome.trim()}`,
    eventoDominio: 'exam.requested',
  })
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'exame_solicitado',
    entidade: 'exames_solicitados',
    registroId: id,
  })
  return id
}

export async function mudarStatusExame(params: {
  workspaceId: string
  exame: ExameSolicitado
  status: StatusExame
  documentoId?: string | null
}): Promise<void> {
  const supabase = createClient()
  const patch: Record<string, unknown> = { status: params.status }
  if (params.documentoId !== undefined) patch.documento_id = params.documentoId
  const { error } = await supabase
    .from('exames_solicitados')
    .update(patch)
    .eq('id', params.exame.id)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error
  await registrarEvento({
    workspaceId: params.workspaceId,
    pacienteId: params.exame.paciente_id,
    consultaId: params.exame.consulta_id,
    tipo: 'exame_status',
    titulo: `Exame ${STATUS_EXAME_LABEL[params.status].toLowerCase()}: ${params.exame.nome}`,
    eventoDominio: params.status === 'recebido' ? 'exam.received' : undefined,
  })
}

/** Resumo do retorno: o que foi solicitado × o que já chegou. */
export function resumoRetorno(exames: ExameSolicitado[]): {
  total: number
  recebidos: number
  pendentes: ExameSolicitado[]
  pronto: 'pronto' | 'parcial' | 'nao_pronto' | 'sem_exames'
} {
  const ativos = exames.filter(e => e.status !== 'cancelado')
  const recebidos = ativos.filter(e => e.status === 'recebido' || e.status === 'analisado')
  const pendentes = ativos.filter(e => e.status === 'solicitado' || e.status === 'realizado')
  return {
    total: ativos.length,
    recebidos: recebidos.length,
    pendentes,
    pronto: ativos.length === 0 ? 'sem_exames'
      : pendentes.length === 0 ? 'pronto'
      : recebidos.length > 0 ? 'parcial' : 'nao_pronto',
  }
}
